import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShippingInfoForm, ShippingInfo } from "@/components/ShippingInfoForm";
import { CustomerTemplate } from "@/components/CustomerTemplateManager";
import { buildProductListFromSettlement } from "@/utils/shippingProductList";
import { useAuth } from "@/hooks/useAuth";
import { useParsingOptions } from "@/hooks/useParsingOptions";
import * as parsedOrderService from "@/supabase/parsedorder/parsedOrderService";
import { toast } from "sonner";
import { logError } from "@/lib/sentry";

export interface ShippingModalHandle {
  /** 배송 정보 입력 모달을 연다. Home 상태를 건드리지 않으므로 Home 본체 재렌더를 유발하지 않는다. */
  open: (customer: CustomerTemplate) => void;
}

interface ShippingModalManagerProps {
  /**
   * 배송정보가 확정적으로 알려졌을 때(저장 성공 또는 저장 게이트의 서버 발견) 부모 캐시를 갱신한다.
   * sessionUserId = 이 모달 세션을 소유한 유저 id — 수신 측이 현재 유저와 대조해 stale 호출을 버린다.
   */
  onShippingInfoKnown: (orderId: number, shippingInfo: Partial<ShippingInfo>, sessionUserId: string) => void;
  /**
   * 화면 진입 시 배치 조회한 배송정보 3상 캐시.
   * undefined = 미커버 · null = 없음 확정 · 객체 = 있음 (useOrderSummaries 참조).
   */
  shippingInfoByOrderId: Map<number, Partial<ShippingInfo> | null>;
}

// 저장 게이트(cached-null 오픈 경로)의 Q1 확인 제한 시간. 초과 시 fail-closed.
const SAVE_GATE_TIMEOUT_MS = 10_000;

// 저장 게이트 결과. 가드 promise는 절대 reject하지 않는다.
// absent = 서버에도 없음(저장 진행 가능) · hydrated = 서버 행 발견 + clean 폼에 반영(이번 저장 중단)
// conflict = 서버 행 발견 + dirty 폼(fail-closed) · failed = 확인 실패/타임아웃(fail-closed)
// stale = 세션 무효(닫힘/재오픈/계정 전환)
type SaveGateResult = 'absent' | 'hydrated' | 'conflict' | 'failed' | 'stale';

const extractOrderId = (customerId: string): number | null => {
  const match = customerId.match(/^order_(\d+)$/);
  return match ? Number(match[1]) : null;
};

/**
 * 배송 정보 입력 모달을 Home에서 분리해 자체 관리한다.
 * - 열기 상태/선택 고객/초기 데이터/자동완성 여부를 내부 state로 보유.
 * - `open(customer)`를 imperative handle로 노출 → Home은 ref 호출만 하므로 모달 오픈 시 Home이 재렌더되지 않는다.
 * - 배송정보가 확정적으로 알려질 때(저장 성공·저장 게이트의 서버 발견)만 `onShippingInfoKnown`으로 부모 캐시를 갱신(필요·저빈도).
 */
export const ShippingModalManager = forwardRef<ShippingModalHandle, ShippingModalManagerProps>(({ onShippingInfoKnown, shippingInfoByOrderId }, ref) => {
  const { user } = useAuth();
  const { parsingOptions, setOptions: setParsingOptions } = useParsingOptions();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerTemplate | null>(null);
  const [initialData, setInitialData] = useState<Partial<ShippingInfo> | undefined>(undefined);
  const [wasAutofilled, setWasAutofilled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // async open()이 항상 최신 값을 읽도록 ref로 보관 (imperative handle을 재생성하지 않기 위함)
  const userRef = useRef(user);
  userRef.current = user;
  const parseProductNameRef = useRef(parsingOptions.parseProductName);
  parseProductNameRef.current = parsingOptions.parseProductName;
  const shippingInfoByOrderIdRef = useRef(shippingInfoByOrderId);
  const onShippingInfoKnownRef = useRef(onShippingInfoKnown);
  useLayoutEffect(() => {
    shippingInfoByOrderIdRef.current = shippingInfoByOrderId;
    onShippingInfoKnownRef.current = onShippingInfoKnown;
  });

  // 오픈 세션 토큰: 닫힘/재오픈/로그아웃 뒤 도착한 stale 오픈 응답이 state·토스트·로그를 발화하지 못하게 게이트
  const openSeqRef = useRef(0);
  // 현재 모달 세션을 연 유저 id — 무중단 계정 전환(A→B) 커밋과 auth-change effect 사이에
  // 끼어든 저장 클릭이 B 계정으로 A 주문에 쓰지 못하게 handleSave에서 대조한다.
  const sessionUserIdRef = useRef<string | null>(null);
  // 저장 중복 방어 락: 세션이 아니라 in-flight 저장 요청이 소유. 해제는 소유 요청의 finally에서만 1회.
  const savingRef = useRef(false);
  // 폼 dirty(수동 입력·자동 파싱 시작) — 늦게 도착한 서버 값/자동완성이 사용자 입력을 덮지 못하게 하는 동기 ref.
  const formDirtyRef = useRef(false);
  // 저장 커밋 후에는 Q2 자동완성이 화면을 바꾸지 못하게 하는 플래그(폼 dirty와 별개 — 플랜 §C).
  const savePendingRef = useRef(false);
  // cached-null 오픈의 저장 게이트. absent/hydrated로 소진되면 null. conflict/failed는 유지(fail-closed).
  const saveGateRef = useRef<{ seq: number; promise: Promise<SaveGateResult> } | null>(null);

  // 모든 닫기 경로(Escape/바깥클릭/X/폼 취소/저장 성공/무효 오픈 시도)의 단일 핸들러.
  // 오픈 seq를 무효화해 닫힌 뒤 도착하는 오픈/하이드레이트 응답과 저장 완료의 닫기 게이트를 stale 처리한다.
  // 저장 락(savingRef)은 건드리지 않음 — 락은 세션이 아니라 in-flight 저장 요청이 소유.
  const closeModal = useCallback(() => {
    openSeqRef.current += 1;
    setIsOpen(false);
  }, []);

  const openModal = useCallback(async (customer: CustomerTemplate) => {
    // 첫 줄에서 시퀀스 증가: 아래 조기 return 분기로 빠져도 이전 in-flight 오픈 응답은 반드시 무효화.
    // 조기 return 분기는 closeModal()로 이전 세션까지 닫는다 — 무효화만 하고 세션을 남기면
    // 이전 셸이 loading 고아 상태로 잔류하거나, 활성 저장 성공이 닫기 게이트를 통과하지 못한다.
    const seq = ++openSeqRef.current;

    const currentUser = userRef.current;
    if (!currentUser) {
      closeModal();
      return;
    }
    const capturedUserId = currentUser.id;

    const orderId = extractOrderId(customer.id);
    if (!orderId) {
      toast.error("유효하지 않은 주문 ID입니다.");
      closeModal();
      return;
    }

    // 셸+스피너 즉시 오픈. 폼은 initialData 확정 후에만 마운트(loading 분기)하므로
    // 병합 의미·폼 동기화·빈 productList 가드는 무변경.
    sessionUserIdRef.current = capturedUserId;
    formDirtyRef.current = false;   // dirty 리셋은 seq 증가 이후에만 (플랜 §C)
    savePendingRef.current = false;
    saveGateRef.current = null;
    setSelectedCustomer(customer);
    setWasAutofilled(false);
    setInitialData(undefined);
    setLoading(true);
    setIsOpen(true);

    const parseProductName = parseProductNameRef.current;

    // 이 오픈 시도가 더 이상 현재 세션이 아니면(재오픈/닫힘/유저 변경) 응답을 전부 폐기
    const isStale = () => seq !== openSeqRef.current || userRef.current?.id !== capturedUserId;

    try {
      // 이 행은 버튼의 '수정' 라벨을 만들 때 이미 배치 조회됐다(3상: 객체/null/undefined).
      // 커버된 행(객체·null)은 클릭 시 네트워크 응답을 기다리지 않는다 — 이 유저의 왕복이
      // 몇 초여도 폼은 즉시 뜬다 (no-await-open 플랜 §B).
      const cachedData = shippingInfoByOrderIdRef.current.get(orderId);

      if (cachedData !== undefined && cachedData !== null) {
        // 양성 캐시: 기존 정보로 폼 즉시 (현행 유지)
        const baseInitialData: Partial<ShippingInfo> = { nickname: customer.nickname };
        if (parseProductName) {
          const productList = buildProductListFromSettlement(customer);
          if (productList) baseInitialData.productName = productList;
        }
        setInitialData({ ...cachedData, ...baseInitialData });
        setWasAutofilled(false);
        setLoading(false);
        return;
      }

      const nicknameTrimmed = (customer.nickname || '').trim();
      const isPlaceholderNickname = nicknameTrimmed === '고객' || nicknameTrimmed === '고객 닉네임';

      if (cachedData === null) {
        // 음성 캐시(없음 확정): 빈 폼 즉시. Q1은 저장 게이트로 강등(폼은 안 막고 저장만 잠근다),
        // Q2 자동완성은 Q1-absent 확정 후에만 발사·적용 (플랜 §B·§C).
        const baseInitialData: Partial<ShippingInfo> = { nickname: customer.nickname };
        if (parseProductName) {
          const productList = buildProductListFromSettlement(customer);
          if (productList) baseInitialData.productName = productList;
        }
        setInitialData(baseInitialData);
        setWasAutofilled(false);
        setLoading(false);

        const gatePromise: Promise<SaveGateResult> = (async () => {
          try {
            const existing = await Promise.race([
              parsedOrderService.getShippingInfoByOrderId(orderId),
              new Promise<'timeout'>((resolve) => setTimeout(() => resolve('timeout'), SAVE_GATE_TIMEOUT_MS)),
            ]);
            // 모든 부수효과는 이 승리 경로에서만 실행 — 타임아웃/stale 이후 늦은 raw 결과는 전부 폐기.
            if (isStale()) return 'stale';
            if (existing === 'timeout') return 'failed';
            if (existing) {
              // 캐시 stale: 서버에 이미 행 존재(다른 탭/기기 저장 등). 캐시 갱신 후
              // clean 폼이면 조용히 hydrate(게이트 소진), dirty면 conflict(fail-closed 유지).
              // Q2는 발사 자체를 하지 않는다(양성/충돌 세션은 자동완성 요청 0).
              onShippingInfoKnownRef.current(orderId, existing, capturedUserId);
              if (!formDirtyRef.current) {
                setInitialData({ ...existing, ...baseInitialData });
                saveGateRef.current = null;
                return 'hydrated';
              }
              return 'conflict';
            }
            // 서버에도 없음 확정 → 저장 진행 가능. Q2 자동완성은 absent 확정 후에만 발사.
            saveGateRef.current = null;
            if (!isPlaceholderNickname) {
              void (async () => {
                const latestByNickname = await parsedOrderService
                  .getLatestShippingInfoByNickname(capturedUserId, customer.nickname)
                  .catch(() => null);
                if (isStale()) return;
                if (formDirtyRef.current || savePendingRef.current) return;
                const hasAutofillFields = latestByNickname && (
                  (latestByNickname.customer_name && latestByNickname.customer_name.trim() !== '') ||
                  (latestByNickname.customer_phone && latestByNickname.customer_phone.trim() !== '') ||
                  (latestByNickname.customer_address && latestByNickname.customer_address.trim() !== '')
                );
                if (!hasAutofillFields) return;
                setInitialData({
                  ...baseInitialData,
                  customer_name: latestByNickname?.customer_name ?? '',
                  customer_phone: latestByNickname?.customer_phone ?? '',
                  customer_address: latestByNickname?.customer_address ?? '',
                  nickname: customer.nickname,
                });
                setWasAutofilled(true);
              })();
            }
            return 'absent';
          } catch (error) {
            if (isStale()) return 'stale';
            logError(error as Error, { context: '배송 정보 확인 실패(저장 게이트)' });
            return 'failed';
          }
        })();
        saveGateRef.current = { seq, promise: gatePromise };
        return;
      }

      // 기존 배송정보와 닉네임 기반 자동완성 후보를 병렬 발사 (race 패턴)
      // - 기존 데이터가 있으면 자동완성 결과는 기다리지 않고 모달을 즉시 오픈 (백그라운드에서 완료, 결과 폐기)
      // - 자동완성 쿼리는 실패해도 모달 오픈을 막지 않도록 별도 catch.
      const existingDataPromise = parsedOrderService.getShippingInfoByOrderId(orderId);
      const latestByNicknamePromise = isPlaceholderNickname
        ? Promise.resolve(null)
        : parsedOrderService
            .getLatestShippingInfoByNickname(currentUser.id, customer.nickname)
            .catch(() => null);

      const existingData = await existingDataPromise;
      if (isStale()) return;

      // Build base initial data: always include nickname from settlement (CustomerTemplateManager)
      const baseInitialData: Partial<ShippingInfo> = { nickname: customer.nickname };
      // If product name extraction is enabled, include a comma-joined product list from settlement.
      // Guard: only set productName when the derived list is non-empty. An empty result means the
      // in-memory order items are stale/empty (the app has no order_item realtime sync), and on the
      // merge below ({ ...existingData, ...baseInitialData }) a '' would clobber a good saved
      // product_name. Omitting the key preserves existingData.productName.
      if (parseProductName) {
        const productList = buildProductListFromSettlement(customer);
        if (productList) {
          baseInitialData.productName = productList;
        }
      }

      const resolvedInitialData = existingData ? { ...existingData, ...baseInitialData } : baseInitialData;

      // 기존 데이터가 있으면 자동완성 결과는 기다리지 않고 폼 확정
      if (existingData) {
        setInitialData(resolvedInitialData);
        setWasAutofilled(false);
        setLoading(false);
        return;
      }

      // 플레이스홀더 닉네임이면 자동 채움 스킵
      if (isPlaceholderNickname) {
        setInitialData(resolvedInitialData);
        setWasAutofilled(false);
        setLoading(false);
        return;
      }

      // 기존 데이터가 없으면 병렬로 가져온 동일 닉네임 최신 parsed_order를 제안용으로 사용
      const latestByNickname = await latestByNicknamePromise;
      if (isStale()) return;
      const hasAutofillFields = latestByNickname && (
        (latestByNickname.customer_name && latestByNickname.customer_name.trim() !== '') ||
        (latestByNickname.customer_phone && latestByNickname.customer_phone.trim() !== '') ||
        (latestByNickname.customer_address && latestByNickname.customer_address.trim() !== '')
      );

      if (hasAutofillFields) {
        const autofillOnlyCoreFields: Partial<ShippingInfo> = {
          customer_name: latestByNickname?.customer_name ?? '',
          customer_phone: latestByNickname?.customer_phone ?? '',
          customer_address: latestByNickname?.customer_address ?? '',
        };
        setInitialData({
          ...resolvedInitialData,
          ...autofillOnlyCoreFields,
          nickname: customer.nickname,
        });
        setWasAutofilled(true);
        setLoading(false);
        return;
      }

      // 제안할 값이 없으면 기본 데이터로 폼 확정
      setInitialData(resolvedInitialData);
      setWasAutofilled(false);
      setLoading(false);
    } catch (error) {
      // stale이면 토스트·logError도 발화 금지 (닫힌 뒤 늦은 실패 응답)
      if (isStale()) return;
      logError(error as Error, { context: "배송 정보 조회 실패" });
      toast.error("기존 배송 정보를 불러오는 데 실패했습니다.");
      // 실패 시에도 닉네임은 반드시 전달, 상품명은 옵션이 켜져 있고 파생 목록이 비어있지 않을 때만 전달
      // (동일한 stale-items 가드: 빈 문자열로 기존 product_name을 덮어쓰지 않도록 키 자체를 생략)
      const fallbackInitialData: Partial<ShippingInfo> = { nickname: customer.nickname };
      if (parseProductName) {
        const productList = buildProductListFromSettlement(customer);
        if (productList) {
          fallbackInitialData.productName = productList;
        }
      }
      setInitialData(fallbackInitialData); // 실패 시 닉네임만이라도 전달 + 옵션 시 상품 목록 포함
      setWasAutofilled(false);
      setLoading(false);
    }
  }, [closeModal]);

  useImperativeHandle(ref, () => ({ open: openModal }), [openModal]);

  // 폼의 수동 입력/자동 파싱 시작 신호 — 미러 state 없이 동기 ref만 세운다.
  const markFormDirty = useCallback(() => {
    formDirtyRef.current = true;
  }, []);

  // 인증 유저 변경(로그아웃/만료/무중단 계정 전환) 시 무효화 + 닫기 — 추가 클릭 없이도
  // in-flight 오픈 응답이 stale UI를 열지 못하고, A 유저의 폼이 B 유저 세션에서 저장되지 않도록.
  // id 비교라 동일 유저의 토큰 리프레시(객체 identity 변경)에는 발화하지 않는다.
  const lastUserIdRef = useRef(user?.id);
  useEffect(() => {
    if (lastUserIdRef.current !== user?.id) {
      lastUserIdRef.current = user?.id;
      closeModal();
    }
  }, [user, closeModal]);

  // 언마운트: 페이지 이탈 후 늦은 오픈/하이드레이트 응답이 토스트·로그를 발화하지 않도록 무효화
  useEffect(() => {
    return () => {
      openSeqRef.current += 1;
    };
  }, []);

  const handleSave = useCallback(async (data: ShippingInfo) => {
    const currentUser = userRef.current;
    if (!currentUser || !selectedCustomer) return;
    // 세션 오너 대조: 계정 전환 직후 auth-change effect가 닫기 전에 끼어든 저장을 차단
    if (currentUser.id !== sessionUserIdRef.current) return;

    const orderId = extractOrderId(selectedCustomer.id);
    if (!orderId) {
      toast.error("유효하지 않은 주문 ID이므로 저장할 수 없습니다.");
      return;
    }

    // 전역 단일 in-flight 락: 렌더 경계 전 재진입(더블 클릭)을 동기 ref로 차단 — 중복 INSERT 방지.
    // 직전 저장이 정산되기 전에는 (재오픈한 모달을 포함해) 다음 저장이 발사되지 않는다.
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    // 저장 커밋: 이후 Q2 자동완성이 커밋된 스냅샷을 바꾸지 못한다 (폼 dirty와 별개, 플랜 §C)
    savePendingRef.current = true;
    // 저장 시작 시점의 오픈 세션 캡처: 성공 시 그 세션이 여전히 활성일 때만 닫는다
    const saveSessionSeq = openSeqRef.current;

    try {
      // cached-null 오픈의 저장 게이트: 서버에 기존 행이 없음이 확인될 때까지 DB 쓰기를 잠근다.
      // 폼 오픈은 막지 않았으므로 타이핑 시간 동안 대부분 이미 resolve돼 있다.
      const gate = saveGateRef.current;
      if (gate && gate.seq === saveSessionSeq) {
        const gateResult = await gate.promise;
        // 게이트 대기 중 세션 변화(닫힘/재오픈/계정 전환) 재검증 — 취소 후 저장 방지.
        // 게이트 없는 경로(양성 캐시/콜드)의 기존 저장 동작은 변하지 않는다.
        if (
          saveSessionSeq !== openSeqRef.current ||
          userRef.current?.id !== sessionUserIdRef.current ||
          userRef.current?.id !== currentUser.id
        ) return;
        if (gateResult === 'stale') return;
        if (gateResult === 'hydrated') {
          // 서버 값이 clean 폼에 반영됨 — 캡처된 이전 스냅샷은 폐기, 유저가 확인 후 다시 저장.
          toast.info('기존 배송정보를 불러왔습니다. 확인 후 다시 저장해주세요.');
          return;
        }
        if (gateResult === 'conflict') {
          // 유저가 서버 값을 보지 못한 채 입력 중 — 덮어쓰기 차단(fail-closed, 게이트 미소진).
          toast.error('기존 배송정보가 있습니다. 닫았다 다시 열어주세요.');
          return;
        }
        if (gateResult === 'failed') {
          toast.error('배송 정보 확인에 실패했습니다. 닫았다 다시 열어주세요.');
          return;
        }
        // 'absent' → 정상 저장 진행
      }

      // persisted = 실제 저장된 행. upsert가 빈 productName을 보존하므로 제출값(data)과 다를 수
      // 있다 — 캐시는 반드시 persisted로 갱신 (제출값 캐시 = 재오픈 시 캐시-DB 괴리).
      const persisted = await parsedOrderService.saveShippingInfoByOrderId(orderId, currentUser.id, selectedCustomer.order_date, data);
      // 성공 토스트·캐시 갱신은 서버 성공 사실 — 세션 stale 여부와 무관하게 항상 발화 (라벨-DB 일관성)
      toast.success(`'${selectedCustomer.nickname}'님의 배송 정보가 저장되었습니다.`);
      if (saveSessionSeq === openSeqRef.current) {
        closeModal();
      }
      // 저장 후 부모 캐시도 persisted로 갱신 — in-flight 중 부모 재렌더로 콜백 identity가
      // 바뀌어도 최신 콜백이 호출되도록 ref 경유 (저장 게이트 경로와 동일 패턴)
      onShippingInfoKnownRef.current(orderId, persisted, currentUser.id);
    } catch (error) {
      logError(error as Error, { context: "배송 정보 저장 실패" });
      toast.error("배송 정보 저장에 실패했습니다.");
    } finally {
      // 락 해제는 소유 요청의 finally에서 무조건 1회 (closeModal은 락을 건드리지 않음)
      savingRef.current = false;
      setSaving(false);
      savePendingRef.current = false;
    }
  }, [selectedCustomer, closeModal]);

  if (!selectedCustomer) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) closeModal(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{selectedCustomer.nickname}님의 배송 정보 입력</DialogTitle>
          <DialogDescription>
            고객 답장을 붙여넣어 자동 정리하기를 하거나, 각 항목을 직접 수정할 수 있습니다.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16">
            <Loader2 className="h-6 w-6 animate-spin text-teal-500" />
            <p className="text-sm text-gray-500">배송 정보 불러오는 중</p>
          </div>
        ) : (
          <>
            {wasAutofilled && (
              <div className="mb-3 rounded-md border border-indigo-100 bg-indigo-50 px-3 py-2 text-sm text-indigo-800">
                이전에 자동 정리된 배송 정보를 자동으로 채웠어요. 확인해주세요.
              </div>
            )}
            <ShippingInfoForm
              initialData={initialData}
              wasAutofilled={wasAutofilled}
              saving={saving}
              onSave={handleSave}
              onClose={closeModal}
              onDirty={markFormDirty}
              parsingOptions={parsingOptions}
              setParsingOptions={setParsingOptions}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
});

ShippingModalManager.displayName = "ShippingModalManager";
